import { useState, useEffect } from 'react';
import { useUI } from '../../store/ui';
import { useCart } from '../../store/cart';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { useLocalizedName } from '../../lib/localizedName';
import { useInvoiceSources } from '../../store/invoiceSources';
import { isMultiUnit, priceForTier, sellingUnitOf } from '../../lib/priceTier';
import { rememberLineNote } from '../cart/recentLineNotes';
import { ensureManagerApproval } from '../held-orders/heldEdit';
import { roundMoney } from '@matix/utils';
import { moneyPrecision } from '../../store/currency';
import { QuantityModifierGroup, type QtyMap } from './QuantityModifierGroup';
import type { CartLineMod } from '../../store/cart';
import type { CatalogItemModifierGroup, CatalogItemModifierOption } from '@matix/types';

type SelectionMap = Record<number, number[]>;
/** group id → its quantities, for "choices with quantity" groups only. */
type GroupQtyMap = Record<number, QtyMap>;

/** A "choices with quantity" group — everything else is an ordinary one. */
const isQtyGroup = (g: CatalogItemModifierGroup): boolean => g.modifier_type === 'quantity';

export function ModifierModal() {
  const isOpen = useUI((s) => s.openModal === 'modifier');
  const item = useUI((s) => s.modifierProduct);
  const meta = useUI((s) => s.modifierMeta);
  const closeModal = useUI((s) => s.closeModal);
  const addLine = useCart((s) => s.addLine);
  const setLineMods = useCart((s) => s.setLineMods);
  // EDIT mode — reopened from a cart line to change its options.
  const editCartId = meta?.editCartId ?? null;
  const editLine = useCart((s) => (editCartId ? s.lines.find((l) => l.cartId === editCartId) ?? null : null));
  const { t } = useLang();
  // One name per line, in the active language. Tenants routinely fill BOTH
  // columns with the same Arabic string, so printing name and name_ar side by
  // side rendered every group and option twice.
  const localized = useLocalizedName();
  // Price this line off the active price list, exactly like the tile the
  // cashier tapped to get here. A null tier means the server's selling_price.
  const tier = useInvoiceSources((s) =>
    s.sources.find((x) => x.id === s.activeId)?.price_tier ?? null,
  );

  const [selections, setSelections] = useState<SelectionMap>({});
  const [qtys, setQtys] = useState<GroupQtyMap>({});
  const [note, setNote] = useState('');

  // Pre-select when the modal opens: the line's own choices and note when
  // editing, else each group's default option.
  useEffect(() => {
    if (!isOpen || !item) return;
    const initial: SelectionMap = {};
    const initialQtys: GroupQtyMap = {};
    const line = editCartId ? useCart.getState().lines.find((l) => l.cartId === editCartId) : undefined;
    for (const g of item.modifier_groups ?? []) {
      if (isQtyGroup(g)) {
        // Quantities come back off the line being edited; a new line starts
        // empty — no default quantity is guessed for anyone.
        const map: QtyMap = {};
        for (const m of line?.mods ?? []) {
          if (g.options.some((o) => o.id === m.option_id)) map[m.option_id] = m.qty ?? 1;
        }
        initialQtys[g.id] = map;
        continue;
      }
      if (line) {
        // Matched by option id; an option the catalog no longer offers is
        // dropped (the window can only show what it can offer).
        const ids = line.mods.filter((m) => g.options.some((o) => o.id === m.option_id)).map((m) => m.option_id);
        if (ids.length > 0) initial[g.id] = ids;
        continue;
      }
      const def = g.options.find((o) => o.is_default && o.is_active);
      if (def) initial[g.id] = [def.id];
    }
    setSelections(initial);
    setQtys(initialQtys);
    // The note belongs to the line being built, not to the modal — reopening
    // for the next item must not inherit the previous customer's request.
    setNote(line?.note ?? '');
  }, [isOpen, item, editCartId]);

  if (!isOpen || !item) return null;

  const groups = item.modifier_groups ?? [];

  function toggle(group: CatalogItemModifierGroup, option: CatalogItemModifierOption) {
    setSelections((prev) => {
      const current = prev[group.id] ?? [];
      if (group.max_selections === 1) {
        return { ...prev, [group.id]: [option.id] };
      }
      if (current.includes(option.id)) {
        return { ...prev, [group.id]: current.filter((id) => id !== option.id) };
      }
      if (current.length >= group.max_selections) return prev;
      return { ...prev, [group.id]: [...current, option.id] };
    });
  }

  /** Options chosen in a group — Min/Max count these in both group types. */
  function chosenCount(g: CatalogItemModifierGroup): number {
    if (isQtyGroup(g)) return Object.values(qtys[g.id] ?? {}).filter((v) => v > 0).length;
    return (selections[g.id] ?? []).length;
  }

  function isGroupValid(g: CatalogItemModifierGroup) {
    return chosenCount(g) >= g.min_selections;
  }

  const allValid = groups.every(isGroupValid);

  function chosenMods(): CartLineMod[] {
    return groups.flatMap((g) => {
      if (isQtyGroup(g)) {
        const map = qtys[g.id] ?? {};
        return g.options
          .filter((o) => (map[o.id] ?? 0) > 0)
          .map((o) => ({
            option_id: o.id,
            group_id: g.id,
            group: g.name,
            idx: g.options.indexOf(o),
            name: o.name,
            nameAr: o.name_ar ?? '',
            // The EXTENDED amount: price per unit × the quantity typed. Every
            // sum over mods[].price (line price, cart, server) then holds.
            price: roundMoney(o.price_delta * map[o.id]!, moneyPrecision()),
            qty: map[o.id]!,
          }));
      }
      const ids = selections[g.id] ?? [];
      return g.options
        .filter((o) => ids.includes(o.id))
        .map((o) => ({
          option_id: o.id,
          group_id: g.id,
          group: g.name,
          idx: g.options.indexOf(o),
          name: o.name,
          nameAr: o.name_ar ?? '',
          price: o.price_delta,
        }));
    });
  }

  /** Same options (by id) and the same note as the line already has? */
  function unchanged(mods: CartLineMod[]): boolean {
    if (!editLine) return false;
    // Quantity is part of the choice (3.5 m vs 4 m); ordinary options are 1.
    const key = (m: CartLineMod): string => `${m.option_id}x${m.qty ?? 1}`;
    const a = editLine.mods.map(key).sort().join(',');
    const b = mods.map(key).sort().join(',');
    return a === b && (editLine.note ?? '').trim() === note.trim();
  }

  async function handleUpdate() {
    if (!allValid || !item || !editLine) return;
    const mods = chosenMods();
    if (unchanged(mods)) {
      closeModal();
      return;
    }
    // A line of a HELD order that is already in the kitchen: new options make
    // it a different dish — the server takes the old one away (a removal,
    // which needs the manager) and sends the new one as ADDED ITEMS. Dropping
    // heldBaseQty makes the till count it as added too, so the save offers to
    // print it.
    const held = useCart.getState().editingHeld && editLine.heldBaseQty != null;
    if (held && !(await ensureManagerApproval())) return;
    setLineMods(editLine.cartId, mods, note);
    if (held) {
      useCart.setState((s) => ({
        lines: s.lines.map((l) => {
          if (l.cartId !== editLine.cartId) return l;
          const { heldBaseQty: _dropped, ...rest } = l;
          return rest;
        }),
      }));
    }
    if (note.trim()) rememberLineNote(note);
    closeModal();
    const shown = localized(item, String(item.code ?? ''));
    toast(t({ en: `Updated ${shown}`, ar: `تم تعديل ${shown}` }), 'success');
  }

  function handleAdd() {
    if (!allValid || !item) return;
    if (editCartId) {
      void handleUpdate();
      return;
    }
    const mods = chosenMods();

    // The unit tile the cashier tapped (a multi-unit item), else the selling
    // unit — the line is priced, labelled and barcoded in that unit.
    const unit = meta?.unit ?? sellingUnitOf(item) ?? null;
    const barcode = unit?.barcode ?? item.units.find((u) => u.barcode)?.barcode ?? item.barcodes?.[0] ?? null;
    const multi = isMultiUnit(item);

    addLine(
      {
        id: item.id,
        name: item.name ?? `#${item.code}`,
        nameAr: item.name_ar ?? '',
        price: priceForTier(item, tier, meta?.unit ?? null),
        cat: 'live',
        emoji: meta?.emoji ?? '',
        bg: meta?.bg ?? '#EDF1F5',
        image: meta?.image ?? null,
        station: meta?.station ?? '',
        code: item.code ?? null,
        barcode,
        restockOnReturn: item.restock_on_return,
        allowDecimals: item.allow_decimals,
        unitId: unit?.unit_id ?? null,
        ...(multi && unit
          ? { unitName: unit.unit_name ?? null, unitNameAr: unit.unit_name_ar ?? null }
          : {}),
      },
      mods,
      // An embedded barcode carries the weight, so the line is added at that
      // quantity rather than at one. Every other way in leaves it unset.
      meta?.qty ?? 1,
      note.trim() || undefined,
    );

    if (note.trim()) rememberLineNote(note);
    closeModal();
    const shown = localized(item, String(item.code ?? ''));
    toast(t({ en: `Added ${shown}`, ar: `تمت إضافة ${shown}` }), 'success');
  }

  return (
    <div
      className="modal-overlay show"
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    >
      <div className="modal" style={{ maxWidth: groups.some(isQtyGroup) ? 600 : 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{localized(item, `#${item.code}`)}</div>
          </div>
          <button className="btn-close" onClick={closeModal} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          {groups.map((g) => {
            const selected = selections[g.id] ?? [];
            const ruleLabel = g.min_selections === 0 ? t({ en: 'optional', ar: 'اختياري' })
              : g.min_selections === g.max_selections ? t({ en: `pick ${g.min_selections}`, ar: `اختر ${g.min_selections}` })
              : `${g.min_selections}–${g.max_selections}`;
            const ruleClass = g.min_selections > 0 ? 'required' : 'optional';
            return (
              <div key={g.id} className="mod-group">
                <div className="mod-group-header">
                  <span className="mod-group-title">{localized(g, `#${g.id}`)}</span>
                  <span className={`mod-group-rule ${ruleClass}`}>{ruleLabel}</span>
                </div>
                {isQtyGroup(g) ? (
                  <QuantityModifierGroup
                    group={g}
                    qtys={qtys[g.id] ?? {}}
                    onChange={(next) => setQtys((prev) => ({ ...prev, [g.id]: next }))}
                  />
                ) : (
                <div className="mod-options">
                  {g.options.filter((o) => o.is_active).map((o) => {
                    const isSel = selected.includes(o.id);
                    const priceLabel = o.price_delta === 0 ? t({ en: 'Free', ar: 'مجاني' })
                      : (o.price_delta > 0 ? '+' : '') + fmt(o.price_delta);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        className={`mod-option ${isSel ? 'selected' : ''}`}
                        onClick={() => toggle(g, o)}
                      >
                        <div className="mod-option-name">{localized(o, `#${o.id}`)}</div>
                        <div className={`mod-option-price ${o.price_delta === 0 ? 'zero' : ''}`}>
                          {priceLabel}
                        </div>
                      </button>
                    );
                  })}
                </div>
                )}
                {!isGroupValid(g) && (
                  <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>
                    {t({ en: `Select at least ${g.min_selections}`, ar: `اختر ${g.min_selections} على الأقل` })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Anything the options can't express — "no onions", "well done".
              Same field the QR-menu customer gets, same destination: the line's
              note on the kitchen ticket. Editable afterwards from the cart. */}
          <div className="mod-group">
            <div className="mod-group-header">
              <span className="mod-group-title">{t({ en: 'Note', ar: 'ملاحظة' })}</span>
              <span className="mod-group-rule optional">{t({ en: 'optional', ar: 'اختياري' })}</span>
            </div>
            <input
              type="text"
              className="kitchen-note"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t({ en: 'Add a note for this item', ar: 'أضف ملاحظة على هذا الصنف' })}
              style={{ minHeight: 0, padding: '10px 12px' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', padding: '12px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={closeModal}>
            {t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!allValid}
            onClick={handleAdd}
          >
            {editCartId
              ? t({ en: 'Update item', ar: 'تعديل الصنف' })
              : t({ en: 'Add to order', ar: 'أضف إلى الطلب' })}
          </button>
        </div>
      </div>
    </div>
  );
}
